import { useEffect, useState, type FormEvent } from "react";
import {
  startRegistration,
  browserSupportsWebAuthn,
  type PublicKeyCredentialCreationOptionsJSON,
} from "@simplewebauthn/browser";
type Key = {
  credential_id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
};
export function PasskeySettings({
  api,
  mutate,
}: {
  api: <T>(path: string) => Promise<T>;
  mutate: <T>(path: string, body: unknown) => Promise<T>;
}) {
  const [keys, setKeys] = useState<Key[]>([]),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [ready, setReady] = useState(false),
    [attempt, setAttempt] = useState(0);
  const [password, setPassword] = useState(""),
    [code, setCode] = useState(""),
    [name, setName] = useState("");
  useEffect(() => {
    let active = true;
    void api<{ items: Key[] }>("/admin/passkeys")
      .then((result) => {
        if (active) {
          setKeys(result.items);
          setReady(true);
        }
      })
      .catch(() => {
        if (active)
          setError("Passkey settings could not load. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [api, attempt]);
  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const options = await mutate<PublicKeyCredentialCreationOptionsJSON>(
        "/admin/passkeys/options",
        { currentPassword: password, code, name: name || "My passkey" },
      );
      const response = await startRegistration({ optionsJSON: options });
      await mutate("/admin/passkeys/verify", response);
      setPassword("");
      setCode("");
      setName("");
      setAttempt((x) => x + 1);
      setMessage(
        "Passkey added. Use Sign in with a passkey on your next visit.",
      );
    } catch (cause) {
      setError(
        (cause as Error).name === "NotAllowedError"
          ? "Passkey setup was canceled or timed out. Try again when you’re ready."
          : (cause as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await mutate(`/admin/passkeys/${encodeURIComponent(id)}/remove`, {
        currentPassword: password,
        code,
      });
      setPassword("");
      setCode("");
      setAttempt((x) => x + 1);
      setMessage("Passkey removed. Other sessions have been signed out.");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="cr-panel" aria-labelledby="passkeys-title">
      <h2 id="passkeys-title">Passkeys</h2>
      <p>
        Sign in with Face ID, Touch ID, Windows Hello, or a security key. Set up
        authenticator MFA below first so your password fallback stays protected.
      </p>
      {error && (
        <p role="alert" className="cr-notice error">
          {error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
      {!browserSupportsWebAuthn() ? (
        <p>
          This browser does not support passkeys. Open the admin site in a
          supported browser.
        </p>
      ) : (
        <form className="cr-form" onSubmit={add}>
          <p>
            Confirm your password and a fresh authenticator or recovery code
            when adding or removing a passkey.
          </p>
          <label>
            Current password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={128}
              required
              disabled={busy}
            />
          </label>
          <label>
            Authenticator or recovery code
            <input
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={50}
              required
              disabled={busy}
            />
          </label>
          <label>
            Passkey name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="For example, Office Mac"
              disabled={busy}
            />
          </label>
          <button className="cr-button" disabled={busy || !ready}>
            {busy ? "Working…" : "Add passkey"}
          </button>
          {keys.length > 0 && (
            <ul className="cr-service-status-list">
              {keys.map((key) => (
                <li key={key.credential_id}>
                  <div>
                    <strong>{key.name}</strong>
                    <p className="cr-form-note">
                      {key.last_used_at
                        ? "Last used " +
                          new Date(key.last_used_at).toLocaleString()
                        : "Not used to sign in yet"}
                    </p>
                  </div>
                  <details>
                    <summary>Remove</summary>
                    <p>
                      Remove this passkey from CRTC? Your password and
                      authenticator remain available.
                    </p>
                    <button
                      type="button"
                      className="cr-button secondary"
                      disabled={busy || !password || !code}
                      onClick={() => void remove(key.credential_id)}
                    >
                      Verify and remove
                    </button>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </form>
      )}
      {!ready && (
        <button
          className="cr-text-button"
          onClick={() => setAttempt((x) => x + 1)}
        >
          Reload passkeys
        </button>
      )}
    </section>
  );
}
